(() => {
  if (customElements.get('machine-editorial-gallery')) return;
  customElements.define('machine-editorial-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      const cards = [...this.querySelectorAll('.machine-gallery-card')];
      if (!cards.length) return;
      const dots = [...this.querySelectorAll('[data-gallery-slide]')];
      const stage = this.querySelector('.machine-gallery-stage');
      const toggle = this.querySelector('[data-gallery-pause]');
      const caption = this.querySelector('.machine-gallery-caption');
      const motion = matchMedia('(prefers-reduced-motion: reduce)');
      let current = 0, playing = !motion.matches, hovered = false, focused = false, touchX = null;
      const on = (target, name, handler) => target.addEventListener(name, handler, {signal});
      const schedule = () => {
        clearTimeout(this.timer);
        if (playing && !hovered && !focused && !document.hidden && cards.length > 1)
          this.timer = setTimeout(() => show(current + 1), 4800);
      };
      const show = index => {
        current = (index + cards.length) % cards.length;
        cards.forEach((card, i) => {
          const offset = (i - current + cards.length) % cards.length;
          const slot = offset === 0 ? 'current' : offset === 1 ? 'next' : offset === cards.length - 1 ? 'previous' : 'hidden';
          card.dataset.slot = slot;
          card.tabIndex = slot === 'hidden' ? -1 : 0;
          card.setAttribute('aria-hidden', String(slot === 'hidden'));
          card.setAttribute('aria-current', String(i === current));
          const video = card.querySelector('video');
          if (video) {
            if (i === current && playing && !motion.matches) video.play().catch(() => {});
            else video.pause();
          }
        });
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
        this.querySelector('[data-gallery-count]').textContent = String(current + 1).padStart(2, '0');
        this.querySelector('[data-gallery-caption]').textContent = cards[current].dataset.caption;
        caption.setAttribute('aria-live', playing && !hovered && !focused ? 'off' : 'polite');
        toggle.textContent = playing ? 'Pause' : 'Play';
        toggle.setAttribute('aria-label', playing ? 'Pause automatic slideshow' : 'Play automatic slideshow');
        toggle.setAttribute('aria-pressed', String(!playing));
        schedule();
      };
      cards.forEach((card, index) => {
        on(card, 'click', () => show(index));
        const media = card.querySelector('img,video');
        const placeholder = card.querySelector('.machine-gallery-placeholder');
        const loaded = () => { media.dataset.loaded = 'true'; placeholder.hidden = true; };
        const failed = () => {
          media.hidden = true; placeholder.hidden = false;
          placeholder.textContent = 'Photograph unavailable — choose another view below';
        };
        if (!media) { placeholder.textContent = 'Choose a photo in the Theme Editor'; return; }
        media.draggable = false;
        on(media, media.tagName === 'VIDEO' ? 'loadeddata' : 'load', loaded);
        on(media, 'error', failed);
        if (media.tagName === 'IMG' && media.complete) media.naturalWidth ? loaded() : failed();
        if (media.tagName === 'VIDEO' && media.readyState >= 2) loaded();
      });
      dots.forEach((dot, i) => on(dot, 'click', () => show(i)));
      on(this.querySelector('[data-gallery-prev]'), 'click', () => show(current - 1));
      on(this.querySelector('[data-gallery-next]'), 'click', () => show(current + 1));
      on(toggle, 'click', () => { playing = !playing; show(current); });
      on(stage, 'mouseenter', () => { hovered = true; schedule(); });
      on(stage, 'mouseleave', () => { hovered = false; schedule(); });
      on(this, 'focusin', () => { focused = true; schedule(); caption.setAttribute('aria-live', 'polite'); });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { focused = false; schedule(); } });
      on(this, 'keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); show(current + (event.key === 'ArrowLeft' ? -1 : 1));
        }
      });
      on(stage, 'touchstart', event => { touchX = event.touches[0]?.clientX ?? null; });
      on(stage, 'touchend', event => {
        if (touchX !== null && event.changedTouches.length) {
          const distance = event.changedTouches[0].clientX - touchX;
          if (Math.abs(distance) > 45) show(current + (distance < 0 ? 1 : -1));
        }
        touchX = null;
      });
      on(stage, 'touchcancel', () => { touchX = null; });
      on(motion, 'change', () => { if (motion.matches) playing = false; show(current); });
      on(document, 'visibilitychange', schedule);
      on(this, 'shopify:block:select', event => {
        const index = cards.findIndex(card => card === event.target || card.contains(event.target));
        if (index >= 0) { playing = false; show(index); }
      });
      show(0);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
})();
